import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { HeadlineFragmentFragmentDoc } from '@packages/gql/generated/shopping/HeadlineFragmentFragmentDoc';
import { Box, ConditionalWrapper, Image, Link, Stack, Typography } from '@packages/shared';
import { useState } from 'react';
import { defineMessage } from 'react-intl';

export const headline = defineMessage({
  id: 'product.brandlogo-headline',
  defaultMessage: 'Art.-Nr.: {data}',
  description: 'The product ordernumber displayed next to the brand logo',
});

/* GraphQL */ `
  fragment HeadlineFragment on Product {
    brand {
      name
      image
    }
    name
    productSeo: seo(locale: $locale) {
      brandLink {
        url
      }
    }
  }
`;

export type ProductHeadlineProps = {
  productData: FragmentType<typeof HeadlineFragmentFragmentDoc>;
};

/**
 * component to display a product headline
 *
 * for context-binding use `ProductHeadlineWithContext`
 */
export const ProductHeadline = ({ productData }: ProductHeadlineProps) => {
  const { brand, productSeo, name } = getFragmentData(HeadlineFragmentFragmentDoc, productData);
  const { brandLink } = { ...productSeo };
  const [brandImage, setBrandImage] = useState(brand?.image ? brand.image : null);

  return (
    <Stack
      sx={{
        pb: 1.5,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {brandImage && (
        <Box
          sx={{
            width: '52px',
            maxHeight: '52px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <ConditionalWrapper
            condition={typeof brandLink?.url === 'string'}
            wrapper={(ch) => (
              <Link href={brandLink!.url!} sx={{ display: 'block' }}>
                {ch}
              </Link>
            )}
          >
            <Image
              src={brandImage}
              alt={brand!.name}
              width="52"
              height="52"
              style={{
                width: 'auto',
                height: 'auto',
                maxWidth: '52px',
                maxHeight: '52px',
                objectFit: 'contain',
                display: 'block',
              }}
              onError={() => setBrandImage(null)}
            />
          </ConditionalWrapper>
        </Box>
      )}
      <Box>
        <Typography variant="h5" component="h2">
          {brand?.name || name}
        </Typography>
      </Box>
    </Stack>
  );
};
